import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";
import {
  useDisconnectSlack,
  useInstallSlack,
  useSetSlackNotifyChannel,
  useSlackChannels,
  useSlackLink,
  useSlackPreferences,
  useSlackStatus,
  useStartSlackLink,
  useUnlinkSlack,
  useUpdateSlackPreferences,
} from "@/hooks/queries/use-slack";
import {
  SLACK_NOTIFICATION_KINDS,
  type SlackIntegrationStatus,
  type SlackNotificationKind,
  type SlackPreferences,
} from "@/lib/api/slack";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EVENT_LABELS: Record<SlackNotificationKind, string> = {
  needs_input: "A build needs my input",
  merge_approval: "A merge is waiting for my approval",
  run_finished: "A build finished",
  run_failed: "A build failed",
  run_stopped: "A build was stopped",
};

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : "Something went wrong.";
}

/**
 * Settings → Slack: an organization admin connects the workspace and picks a
 * channel for build updates; every member links their own Slack account and
 * chooses which events DM them, with optional quiet hours.
 */
export function SlackCard() {
  const { data: status, isLoading } = useSlackStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const result = searchParams.get("slack");

  /** The OAuth callback lands on /settings?slack=connected|error; show it once. */
  const [banner] = useState(result);
  useEffect(() => {
    if (!result) return;
    const next = new URLSearchParams(searchParams);
    next.delete("slack");
    setSearchParams(next, { replace: true });
  }, [result, searchParams, setSearchParams]);

  if (isLoading || !status) return <div className="h-16 animate-pulse rounded-lg bg-muted" />;

  if (!status.configured) {
    return <p className="text-sm text-muted-foreground">Connecting Slack isn't available on this CompBox server yet.</p>;
  }

  return (
    <div className="space-y-6">
      {banner === "connected" && (
        <p role="status" className="flex items-center gap-2 text-sm text-emerald-500">
          <CheckCircle2 className="h-4 w-4" /> Slack is connected.
        </p>
      )}
      {banner === "error" && (
        <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" /> Slack couldn't be connected. Please try again.
        </p>
      )}

      <WorkspaceSection status={status} />
      {status.installation && <PersonalSection />}
    </div>
  );
}

function WorkspaceSection({ status }: { status: SlackIntegrationStatus }) {
  const install = useInstallSlack();
  const disconnect = useDisconnectSlack();
  const { installation, canManage } = status;

  if (!installation) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          The CompBox app for Slack messages people when a build needs their input or a merge is waiting, and lets
          them answer without leaving Slack.
        </p>
        {canManage ? (
          <>
            <Button size="sm" onClick={() => { install.mutate(); }} disabled={install.isPending}>
              {install.isPending ? "Opening Slack…" : "Connect Slack"}
            </Button>
            {install.isError && <p className="text-sm text-destructive">{errorText(install.error)}</p>}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Ask an organization admin to connect Slack.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/[0.08] p-3">
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <span>
            Connected to <span className="font-medium">{installation.slackTeamName}</span>
          </span>
        </div>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            disabled={disconnect.isPending}
            onClick={() => {
              if (window.confirm(`Disconnect ${installation.slackTeamName}? Everyone's Slack link will be removed.`)) {
                disconnect.mutate();
              }
            }}
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </Button>
        )}
      </div>
      {disconnect.isError && <p className="text-sm text-destructive">{errorText(disconnect.error)}</p>}

      {canManage ? (
        <NotifyChannelPicker current={installation.notifyChannel} />
      ) : (
        installation.notifyChannel && (
          <p className="text-sm text-muted-foreground">
            Build updates are posted to <span className="font-medium">#{installation.notifyChannel.name}</span>.
          </p>
        )
      )}
    </div>
  );
}

function NotifyChannelPicker({ current }: { current: { id: string; name: string } | null }) {
  const { data, isLoading, isError, error } = useSlackChannels(true);
  const setChannel = useSetSlackNotifyChannel();
  const channels = data?.channels ?? [];
  // Keep the current channel selectable even if it fell outside the listed pages.
  const options = current && !channels.some((c) => c.id === current.id) ? [current, ...channels] : channels;

  return (
    <div className="space-y-2">
      <Label htmlFor="slack-notify-channel">Channel for build updates</Label>
      <select
        id="slack-notify-channel"
        className={SELECT_CLASS}
        value={current?.id ?? ""}
        disabled={isLoading || setChannel.isPending}
        onChange={(e) => { setChannel.mutate(e.target.value || null); }}
      >
        <option value="">None</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            #{c.name}
          </option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">
        Finished and failed builds are posted here for the whole organization. Only public channels are listed.
        {data?.truncated && " Not every channel could be listed."}
      </p>
      {isError && <p className="text-sm text-destructive">{errorText(error)}</p>}
      {setChannel.isError && <p className="text-sm text-destructive">{errorText(setChannel.error)}</p>}
    </div>
  );
}

function PersonalSection() {
  const { data: linkStatus, isLoading } = useSlackLink();
  const start = useStartSlackLink();
  const unlink = useUnlinkSlack();
  const link = linkStatus?.link ?? null;

  if (isLoading || !linkStatus) return <div className="h-10 animate-pulse rounded-lg bg-muted" />;

  return (
    <div className="space-y-4 border-t border-white/[0.08] pt-4">
      <h3 className="text-sm font-medium">Your Slack account</h3>

      {link ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Linked — CompBox will DM you in Slack.
          </p>
          <Button size="sm" variant="outline" disabled={unlink.isPending} onClick={() => { unlink.mutate(); }}>
            {unlink.isPending ? "Unlinking…" : "Unlink"}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Link your Slack account to get messages about your builds.</p>
          {start.data ? (
            <div className="space-y-2 rounded-lg border border-white/[0.08] p-3 text-sm">
              <p>{start.data.instructions}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" asChild>
                  <a href={start.data.url}>Open Slack</a>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href={start.data.webUrl} target="_blank" rel="noreferrer">
                    Open in browser <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" onClick={() => { start.mutate(); }} disabled={start.isPending}>
              {start.isPending ? "Starting…" : "Link Slack account"}
            </Button>
          )}
        </div>
      )}
      {start.isError && <p className="text-sm text-destructive">{errorText(start.error)}</p>}
      {unlink.isError && <p className="text-sm text-destructive">{errorText(unlink.error)}</p>}

      {link && <PreferencesForm />}
    </div>
  );
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function PreferencesForm() {
  const { data: prefs, isLoading } = useSlackPreferences(true);
  const update = useUpdateSlackPreferences();

  if (isLoading || !prefs) return <div className="h-24 animate-pulse rounded-lg bg-muted" />;

  return (
    <div className="space-y-4">
      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium">Message me when</legend>
        {SLACK_NOTIFICATION_KINDS.map((kind) => (
          <div key={kind} className="flex items-center gap-2">
            <Checkbox
              id={`slack-event-${kind}`}
              checked={prefs.events[kind]}
              disabled={update.isPending}
              onCheckedChange={(checked) => { update.mutate({ events: { [kind]: checked } }); }}
            />
            <Label htmlFor={`slack-event-${kind}`} className="font-normal">
              {EVENT_LABELS[kind]}
            </Label>
          </div>
        ))}
      </fieldset>

      <QuietHoursForm key={JSON.stringify(prefs.quietHours)} prefs={prefs} />
      {update.isError && <p className="text-sm text-destructive">{errorText(update.error)}</p>}
    </div>
  );
}

function QuietHoursForm({ prefs }: { prefs: SlackPreferences }) {
  const update = useUpdateSlackPreferences();
  const saved = prefs.quietHours;
  const [enabled, setEnabled] = useState(saved !== null);
  const [start, setStart] = useState(saved?.start ?? "22:00");
  const [end, setEnd] = useState(saved?.end ?? "08:00");
  const [tz, setTz] = useState(saved?.tz ?? browserTimeZone());

  const sameTimes = enabled && start === end;
  const dirty = enabled
    ? saved?.start !== start || saved.end !== end || saved.tz !== tz
    : saved !== null;

  return (
    <fieldset className="space-y-3">
      <div className="flex items-center gap-2">
        <Checkbox id="slack-quiet-hours" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="slack-quiet-hours" className="font-normal">
          Quiet hours — hold messages until they end
        </Label>
      </div>
      {enabled && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="slack-quiet-start">From</Label>
            <Input id="slack-quiet-start" type="time" value={start} onChange={(e) => { setStart(e.target.value); }} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="slack-quiet-end">Until</Label>
            <Input id="slack-quiet-end" type="time" value={end} onChange={(e) => { setEnd(e.target.value); }} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="slack-quiet-tz">Time zone</Label>
            <Input id="slack-quiet-tz" value={tz} onChange={(e) => { setTz(e.target.value); }} />
          </div>
        </div>
      )}
      {sameTimes && <p className="text-sm text-destructive">Start and end must differ.</p>}
      {dirty && (
        <Button
          size="sm"
          disabled={update.isPending || sameTimes || (enabled && !tz.trim())}
          onClick={() => { update.mutate({ quietHours: enabled ? { start, end, tz: tz.trim() } : null }); }}
        >
          {update.isPending ? "Saving…" : "Save quiet hours"}
        </Button>
      )}
      {update.isError && <p className="text-sm text-destructive">{errorText(update.error)}</p>}
    </fieldset>
  );
}
